import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { QUERIES } from '../constants/mediaQueries';
import { ColorScheme } from '../config/colorSchemes';
import Button from './Button';

interface ScrollToTopButtonProps {
  // Pixels scrolled before button appears
  threshold?: number;
  // Target element ID to scroll to (empty for page top)
  targetId?: string;
}

const ButtonContainer = styled.div`
  position: fixed;
  bottom: 5rem;
  right: 2rem;
  z-index: 99;
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.3s ease,
    visibility 0.3s ease;

  &.visible {
    opacity: 1;
    visibility: visible;
  }

  ${QUERIES.maxMobile} {
    right: 1.5rem;
  }
`;

// Button that appears when scrolling down to easily return to the top
const ScrollToTopButton: React.FC<ScrollToTopButtonProps> = ({
  threshold = 400,
  targetId = '',
}) => {
  const [isVisible, setIsVisible] = useState(false);

  // Toggle visibility based on scroll position
  useEffect(() => {
    const toggleVisibility = () => {
      setIsVisible(window.scrollY > threshold);
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, [threshold]);

  // Handle click - scroll to target or top
  const scrollToTop = () => {
    if (targetId) {
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }

    // Update URL hash
    if (targetId) {
      window.history.replaceState(null, '', `#${targetId}`);
    } else {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  };

  return (
    <ButtonContainer className={isVisible ? 'visible' : ''}>
      <Button
        aria-label="Scroll to top"
        colorScheme="scheme2"
        iconName="HiMiniArrowUp"
        iconPosition="icon-only"
        iconSize="xxlarge"
        onClick={scrollToTop}
      />
    </ButtonContainer>
  );
};

export default ScrollToTopButton;
